/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useMemo } from 'react';
import moment from 'moment';

import Styled from './styles';
import useHook from './hook';
import useCurrentSnap from '../../../../core/hooks/useCurrentSnap';
import { Typography } from '../../../../shared/typography';
import chat from '../../../../assets/images/chat.svg';
import rooms from '../../../../assets/images/add-contact.svg';
import share from '../../../../assets/images/share.svg';
import deleteIcon from '../../../../assets/images/new-delete.svg';
import Comentators from '../../../../shared/comentators';
import Comments from '../comments';
import { Tooltip } from '@mui/material';

type IProps = {
	item: any;
	originalName: any;
	activeTab: string;
};

const Chart: FC<IProps> = ({ item, originalName, activeTab }) => {
	const {
		state,
		toggleChat,
		closeChat,
		onDeleteMine,
		onDeleteShared,
		onOpenInvites,
		onShare,
	} = useHook();
	const { currentChart } = useCurrentSnap({ item });
	const { Component, defaultFields, adapter, ...rest } = currentChart || {};
	const {
		capturedInput,
		updatedAt,
		commentators,
		description,
		timeRange,
		sorting,
	} = item;
	const SALESFORCE_ICON =
		'https://getrevvy.s3.us-east-2.amazonaws.com/logos/salesforce.png';
	const OUTREACH_ICON =
		'https://getrevvy.s3.us-east-2.amazonaws.com/logos/outreach.png';
	const {
		isVisibleChat,
		isChatLoading,
		isDeleting,
		isLoadingMembers,
		isCopied,
	} = state;
	const showNoData = useMemo(
		() =>
			Array.isArray(item?.responsePayload)
				? !item?.responsePayload?.length
				: !item?.responsePayload?.itemsList.length,
		[item]
	);
	const modalOptions = useMemo(
		() => ({
			'Created by me': [
				{
					title: 'Open chat',
					image: <img src={chat} style={{ width: 18 }} alt="" />,
					callback: (type: string, item: any) => toggleChat(item),
					isLoading: isChatLoading,
				},
				{
					title: 'Add team members',
					image: <img src={rooms} style={{ width: 20 }} alt="" />,
					callback: (type: string, item: any) => onOpenInvites(item._id),
					isLoading: isLoadingMembers,
				},
				{
					title: isCopied ? 'Copied' : 'Share',
					image: <img src={share} style={{ width: 15 }} alt="" />,
					callback: (type: string, item: any) => onShare(item),
					isLoading: false,
				},
				{
					title: 'Delete',
					image: <img src={deleteIcon} style={{ width: 18 }} alt="" />,
					callback: (type: string, item: any) => onDeleteMine(item._id),
					isLoading: isDeleting,
				},
			],
			'Shared with me': [
				{
					title: 'Open chat',
					image: <img src={chat} style={{ width: 15 }} alt="" />,
					callback: (type: string, item: any) => toggleChat(item),
					isLoading: isChatLoading,
				},
				{
					title: 'Delete',
					image: <img src={deleteIcon} style={{ width: 15 }} alt="" />,
					callback: (type: string, item: any) => onDeleteShared(item._id),
					isLoading: isDeleting,
				},
			],
		}),
		[state]
	);
	const options = modalOptions[activeTab as keyof typeof modalOptions];

	const date = moment(updatedAt).format('DD.MM.YY');

	return (
		<Styled.Wrapper>
			<Styled.Header>
				<div style={{ width: '70%' }}>
					<Typography type="king" size="chartTitle">
						{capturedInput}
					</Typography>
					<div style={{ display: 'flex', alignItems: 'center', marginTop: 15 }}>
						{!item?.source || item.source === 'salesforce' ? (
							<img
								src={SALESFORCE_ICON}
								style={{ width: 33, marginRight: 15 }}
								alt="salesforce icon"
							/>
						) : (
							<img
								src={OUTREACH_ICON}
								style={{ width: 33, marginRight: 15 }}
								alt="outreach icon"
							/>
						)}
						<Typography size="normal" color="black">
							{`Created: ${date}`}
						</Typography>
					</div>
				</div>
				<Styled.Top>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end',
							flex: 2,
						}}
					>
						<Styled.Options>
							{commentators?.length ? <Comentators item={item} /> : null}
							<Styled.CircleButtonsWrapper>
								{options.map((option: any) => (
									<Tooltip title={option.title} arrow>
										<Styled.CircleButton
											onClick={() => option.callback(originalName, item)}
										>
											{option.image}
										</Styled.CircleButton>
									</Tooltip>
								))}
							</Styled.CircleButtonsWrapper>
							<Styled.Chat isVisibleChat={isVisibleChat}>
								<Comments closeChat={closeChat} item={item} />
							</Styled.Chat>
						</Styled.Options>
					</div>
				</Styled.Top>
			</Styled.Header>
			{!!description && (
				<Styled.Header>
					<Styled.Title>
						<Typography type="king" size="large">
							{description}
						</Typography>
					</Styled.Title>
				</Styled.Header>
			)}
			<Styled.StatusContainer>
				{timeRange?.field && timeRange?.from && timeRange?.to && (
					<Styled.Status color="btnGreen">
						<Typography color={'black'} size="normal" type="bold">
							{`${timeRange?.field?.replace(/([A-Z])/g, ' $1').trim()}: `}
						</Typography>
						<Typography color={'black'} size="normal">
							{`${moment(timeRange?.from).format('D.M.YY')} - ${moment(
								timeRange?.to
							).format('D.M.YY')}`}
						</Typography>
					</Styled.Status>
				)}
				{sorting?.field &&
					sorting?.order &&
					sorting?.order !== 'No Sorting' && (
						<Styled.Status color="btnGreen">
							<Typography color={'black'} size="normal" type="bold">
								{'Sorting: '}
							</Typography>
							<Typography color={'black'} size="normal">
								{`${sorting?.field
									?.replace(/([A-Z])/g, ' $1')
									.trim()
									.toLowerCase()} by ${sorting?.order?.toLowerCase()}`}
							</Typography>
						</Styled.Status>
					)}
			</Styled.StatusContainer>
			{!showNoData && Component && (
				<Component
					data={adapter()}
					defaultFields={defaultFields}
					isPadding={false}
					isDetail={true}
					name={originalName.capturedInput}
					{...rest}
				/>
			)}
			{showNoData && (
				<Styled.NoDataBlock>
					<Typography type="king" size={'king'}>
						No records found. Try changing criteria
					</Typography>
				</Styled.NoDataBlock>
			)}
		</Styled.Wrapper>
	);
};

export default React.memo(Chart);
